import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ScienceIcon from '@mui/icons-material/Science';
import { useAppStore } from '../store/useAppStore';
import * as repo from '../db/repository';
import { newId } from '../utils/id';
import { DOCUMENT_ACCEPT } from '../utils/documentImport';
import {
  savePrimaryApplicationFile,
  startApplicationIntake,
} from '../services/profileIntakeService';
import SpeechStatusNote from './SpeechStatusNote';
import BrandLogo from './BrandLogo';
import { isHostedWeb } from '../config/cloud';
import { useAuth } from '../auth/auth';
import { EMPTY_PROFILE_INPUTS, EMPTY_SCHOOL_FIT } from '../types';

/**
 * First-run wizard. The hosted web portal uses managed cloud AI, so it starts
 * straight at "About you"; the desktop build keeps the Codex CLI check first.
 * The "About you" step cannot be skipped — the app needs at least a name so
 * coaching and grading are personal — but the application upload and the first
 * school stay optional. Question research moved to the dashboard checklist.
 */
export default function Onboarding() {
  const {
    provider,
    ctx,
    aiStatus,
    checkAiStatus,
    refreshProfile,
    refreshPrograms,
    completeOnboarding,
    showToast,
  } = useAppStore();
  const hostedWeb = isHostedWeb();
  const auth = useAuth();

  const steps = hostedWeb ? ['About you', 'Your first school'] : ['AI setup', 'About you', 'Your first school'];
  /** Index of the "About you" step in the active step list. */
  const aboutStep = hostedWeb ? 0 : 1;
  const schoolStep = aboutStep + 1;

  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  // AI check (desktop only)
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  // About you — name is required; application upload is optional.
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState(false);
  const [appFile, setAppFile] = useState<{ profileId: string; filename: string } | null>(null);
  const [whyDentistry, setWhyDentistry] = useState('');

  // First school
  const [schoolName, setSchoolName] = useState('');
  const [shortName, setShortName] = useState('');

  useEffect(() => {
    if (!hostedWeb && aiStatus.state === 'unknown') void checkAiStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Signed-in web users already told us their name — prefill, still editable.
  useEffect(() => {
    if (!name && auth.user?.name) setName(auth.user.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.user?.name]);

  const runFullTest = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      await checkAiStatus();
      setTestResult(await provider.testConnection());
    } finally {
      setBusy(false);
    }
  };

  const importApplication = async (file?: File) => {
    if (!file) return;
    setBusy(true);
    try {
      const profileId = await savePrimaryApplicationFile(file);
      setAppFile({ profileId, filename: file.name });
      showToast(`Saved ${file.name} — it stays downloadable from Student Profile.`, 'info');
    } catch (e) {
      showToast(`Import failed: ${(e as Error).message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  /** Persist the "About you" step; returns false when the name is missing. */
  const saveProfile = (): boolean => {
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError(true);
      return false;
    }
    const existing = repo.getActiveProfile();
    repo.upsertProfile({
      id: existing?.id ?? newId('prof'),
      name: trimmed,
      inputs: { ...EMPTY_PROFILE_INPUTS, ...existing?.inputs, whyDentistry },
      structured: existing?.structured ?? null,
      createdAt: '',
      updatedAt: '',
    });
    refreshProfile();
    // Populate every profile field from the application in the background —
    // the wizard (and the whole app) stays usable while the AI reads it.
    if (appFile && (hostedWeb || aiStatus.state === 'ready')) {
      if (startApplicationIntake(appFile.profileId, appFile.filename)) {
        showToast(
          'Reading your application in the background — your profile fields will fill in automatically and you will be notified.',
          'info',
        );
      }
    }
    return true;
  };

  const addSchool = async (withAi: boolean) => {
    const trimmed = schoolName.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      const fit = withAi ? await provider.buildSchoolFit(trimmed, ctx) : { ...EMPTY_SCHOOL_FIT };
      const created = repo.upsertProgram({
        id: newId('prog'),
        name: trimmed,
        shortName: shortName.trim() || trimmed.split(' ')[0],
        fit,
        interviewDate: null,
        createdAt: '',
      });
      refreshPrograms();
      showToast(`Added ${created.shortName}.`, 'success');
      completeOnboarding();
    } catch (e) {
      showToast(`Could not add school: ${(e as Error).message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  const aiChip =
    aiStatus.state === 'ready' ? (
      <Chip icon={<CheckCircleIcon />} color="success" label={`Codex CLI found — ${aiStatus.detail}`} />
    ) : aiStatus.state === 'checking' ? (
      <Chip icon={<CircularProgress size={14} />} label="Checking for the Codex CLI…" />
    ) : (
      <Chip icon={<ErrorOutlineIcon />} color="warning" label="Codex CLI not found" />
    );

  const aiReady = hostedWeb || aiStatus.state === 'ready';

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, sm: 6 }, px: { xs: 2, sm: 3 } }}>
      <Stack spacing={0.75} sx={{ mb: 3 }}>
        <BrandLogo size={40} nameSx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
        <Typography variant="body2" color="text.secondary">
          Let&apos;s set up your prep in a couple of minutes — everything can be changed later.
        </Typography>
      </Stack>

      {/* Phones get a compact "Step n of N" indicator; the full stepper needs the width. */}
      <Box sx={{ display: { xs: 'none', sm: 'block' }, mb: 3 }}>
        <Stepper activeStep={step}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ display: { xs: 'flex', sm: 'none' }, mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Step {step + 1} of {steps.length}
        </Typography>
        <Typography variant="subtitle2">{steps[step]}</Typography>
      </Stack>

      {!hostedWeb && step === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              The AI runs through the Codex CLI on this computer
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Profile extraction, school research, answer coaching, and grading all call the <strong>Codex CLI</strong>{' '}
              locally — nothing is uploaded to this app's servers (it has none). You need the CLI installed and signed in.
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
              {aiChip}
              <Button size="small" onClick={() => void checkAiStatus()} disabled={aiStatus.state === 'checking'}>
                Check again
              </Button>
            </Stack>

            {aiStatus.state === 'unavailable' && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Install the Codex CLI, sign in, then click "Check again":
                </Typography>
                <Box component="pre" sx={{ m: 0, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, fontSize: 13 }}>
                  {'npm install -g @openai/codex\ncodex login'}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Installed somewhere unusual? Set the full executable path later in Settings → AI Provider.
                  Detail: {aiStatus.detail}
                </Typography>
              </Alert>
            )}

            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="outlined"
                startIcon={<ScienceIcon />}
                onClick={runFullTest}
                disabled={busy || aiStatus.state !== 'ready'}
              >
                Run a full test call
              </Button>
              {busy && <CircularProgress size={18} />}
            </Stack>
            {testResult && (
              <Alert severity={testResult.ok ? 'success' : 'error'} sx={{ mt: 2 }}>
                {testResult.message}
              </Alert>
            )}

            <SpeechStatusNote sx={{ mt: 2 }} />

            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
              <Button variant="contained" onClick={() => setStep(1)}>
                Continue
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {step === aboutStep && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Tell us about you
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your name personalizes the whole app. If you upload the primary application you submitted
              (e.g. the AADSAS PDF), the AI reads it in the background and fills in your Student Profile —
              experiences, shadowing hours, personal statement, and more — so every coached answer sounds
              like you. The file stays private to your account.
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Your name"
                required
                size="small"
                value={name}
                error={nameError}
                helperText={nameError ? 'Please enter your name — the coach uses it to personalize everything.' : undefined}
                onChange={(e) => {
                  setName(e.target.value);
                  if (e.target.value.trim()) setNameError(false);
                }}
                placeholder="e.g. Alex Rivera"
              />
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button
                  component="label"
                  variant={appFile ? 'text' : 'outlined'}
                  startIcon={<UploadFileIcon />}
                  disabled={busy}
                >
                  {appFile ? 'Replace file' : 'Upload primary application (optional)'}
                  <input
                    type="file"
                    hidden
                    accept={DOCUMENT_ACCEPT}
                    onChange={(e) => void importApplication(e.target.files?.[0])}
                  />
                </Button>
                {busy && <CircularProgress size={18} />}
                {appFile && <Chip icon={<CheckCircleIcon />} color="success" variant="outlined" label={appFile.filename} />}
              </Stack>
              <TextField
                label="Why dentistry? (optional — one paragraph is fine)"
                value={whyDentistry}
                onChange={(e) => setWhyDentistry(e.target.value)}
                fullWidth
                multiline
                minRows={3}
              />
            </Stack>
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
              <Button
                variant="contained"
                disabled={busy}
                onClick={() => {
                  if (saveProfile()) setStep(schoolStep);
                }}
              >
                Save & Continue
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {step === schoolStep && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Add your first dental school
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {aiReady
                ? 'We build a school-fit profile (mission, curriculum, interview style) in a few seconds. You can add more schools any time from Dental Programs.'
                : 'The Codex CLI is not set up, so the school is added without a fit profile — refresh it later from Dental Programs.'}
            </Typography>
            <Stack spacing={2} sx={{ maxWidth: 480 }}>
              <TextField
                label="Full program name"
                size="small"
                value={schoolName}
                onChange={(e) => setSchoolName(e.target.value)}
                placeholder="University of Florida College of Dentistry"
                disabled={busy}
              />
              <TextField
                label="Short name (e.g. UF)"
                size="small"
                value={shortName}
                onChange={(e) => setShortName(e.target.value)}
                disabled={busy}
              />
            </Stack>
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }} alignItems="center">
              <Button onClick={completeOnboarding} disabled={busy}>
                Skip for now
              </Button>
              <Stack direction="row" spacing={1} alignItems="center">
                {busy && <CircularProgress size={18} />}
                <Button
                  variant="contained"
                  disabled={busy || !schoolName.trim()}
                  onClick={() => void addSchool(aiReady)}
                >
                  {aiReady ? 'Add School & Finish' : 'Add School'}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Container>
  );
}
